function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes(arr) {
    if (Array.isArray(arr)) return arr;
}
function _iterable_to_array_limit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _sliced_to_array(arr, i) {
    return _array_with_holes(arr) || _iterable_to_array_limit(arr, i) || _unsupported_iterable_to_array(arr, i) || _non_iterable_rest();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
import React, { createContext, useState, useEffect, useRef } from "react";
import { Root as DialogPrimitiveRoot, Content as DialogPrimitiveContent, Portal as DialogPrimitivePortal, Overlay as DialogPrimitiveOverlay } from "@radix-ui/react-dialog";
import PropTypes from "prop-types";
import cc from "classcat";
import ModalClose from "./ModalClose";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { twMerge } from "tailwind-merge";
import { useModalManagerContext } from "../../app/ModalManagerContext";
Modal.propTypes = {
    children: PropTypes.node,
    onClose: PropTypes.func,
    dismissible: PropTypes.bool,
    forceMount: PropTypes.bool,
    centered: PropTypes.bool,
    size: PropTypes.string,
    open: PropTypes.bool,
    isHidden: PropTypes.bool.isRequired,
    onOpenAutoFocus: PropTypes.func,
    onModalClose: PropTypes.func
};
export var ModalContext = /*#__PURE__*/ createContext(null);
export default function Modal(param) {
    var children = param.children, _param_dismissible = param.dismissible, dismissible = _param_dismissible === void 0 ? true : _param_dismissible, _param_centered = param.centered, centered = _param_centered === void 0 ? true : _param_centered, _param_size = param.size, size = _param_size === void 0 ? "xs" : _param_size, _param_onOpenAutoFocus = param.onOpenAutoFocus, onOpenAutoFocus = _param_onOpenAutoFocus === void 0 ? null : _param_onOpenAutoFocus, isHidden = param.isHidden, onModalClose = param.onModalClose;
    var _useModalManagerContext = useModalManagerContext(), isOpen = _useModalManagerContext.isOpen, goToNextModal = _useModalManagerContext.goToNextModal;
    var _useState = _sliced_to_array(useState(false), 2), fadeIn = _useState[0], setFadeIn = _useState[1];
    var fadeDuration = 200;
    var onCrossButtonClick = function(e) {
        e.preventDefault();
        onModalClose();
    };
    function onClose() {
        if (!fadeIn || !dismissible) {
            return;
        }
        setFadeIn(false);
        setTimeout(function() {
            goToNextModal();
        }, fadeDuration);
    }
    useEffect(function() {
        if (isOpen) {
            !isHidden && setFadeIn(true);
        } else {
            onClose();
        }
    }, [
        isOpen
    ]);
    /**
     * We need this so we can trap the focus inside the modal on mount,
     * otherwise, the focus won't be trapped in the modal when it's mounted and we can navigate through the
     * elements behind the modal using the keyboard.
     */ var modalFocusRef = useRef(null);
    var firstTimeRef = useRef(true);
    useEffect(function() {
        var _modalFocusRef_current;
        (_modalFocusRef_current = modalFocusRef.current) === null || _modalFocusRef_current === void 0 ? void 0 : _modalFocusRef_current.focus();
        if (firstTimeRef.current) {
            firstTimeRef.current = false;
        }
    }, []);
    return /*#__PURE__*/ React.createElement(ModalContext.Provider, {
        value: {
            fadeIn: fadeIn,
            onClose: onClose,
            fadeDuration: fadeDuration,
            centered: centered
        }
    }, /*#__PURE__*/ React.createElement(DialogPrimitiveRoot, {
        open: true
    }, /*#__PURE__*/ React.createElement(DialogPrimitivePortal, null, /*#__PURE__*/ React.createElement(DialogPrimitiveOverlay, {
        // please do not remove scroll-to-error-cls, is used to identify the correct container in useScrollToError
        className: cc([
            "scroll-to-error-cls fixed inset-0 z-50 scale-100 overflow-y-auto bg-black/50 transition-opacity dark:bg-black/60 duration-".concat(fadeDuration),
            {
                "opacity-0": !fadeIn
            },
            {
                "opacity-100": fadeIn
            },
            {
                invisible: isHidden
            }
        ])
    }, /*#__PURE__*/ React.createElement("div", {
        className: twMerge(cc([
            "pointer-events-none relative mx-auto my-0 flex  items-start justify-center opacity-100 md:px-2 md:pb-5 md:mb-[75px] 4xl:mb-[175px]",
            {
                "md:max-w-xl": size === "xs",
                "md:max-w-2xl": size === "sm",
                "md:max-w-4xl": size === "md",
                "md:max-w-7xl": size === "lg",
                "md:max-w-full md:px-10 xl:px-40 3xl:px-64": size === "full"
            }
        ]))
    }, /*#__PURE__*/ React.createElement(DialogPrimitiveContent, {
        onEscapeKeyDown: function(event) {
            return event.preventDefault();
        },
        onPointerDownOutside: function(event) {
            return event.preventDefault();
        },
        onOpenAutoFocus: function(event) {
            event.preventDefault();
            if (onOpenAutoFocus) {
                onOpenAutoFocus();
            }
        },
        className: twMerge(cc([
            "pointer-events-auto relative flex min-h-screen w-full flex-col items-center justify-start border bg-white pt-12 opacity-100 outline-none transition-all dark:border-gray-700 dark:bg-blue-800 md:min-h-fit md:rounded-md md:pt-8 duration-".concat(fadeDuration),
            {
                "relative top-[-50px] 4xl:top-[-100px] opacity-0 md:top-[0px]": !fadeIn
            },
            {
                "relative top-0 opacity-100 md:top-[50px] 4xl:top-[100px]": fadeIn
            }
        ]))
    }, firstTimeRef.current && /*#__PURE__*/ React.createElement("span", {
        tabIndex: 0,
        ref: modalFocusRef,
        className: "pointer-events-none opacity-0 outline-none",
        "aria-hidden": true
    }), children, /*#__PURE__*/ React.createElement(ModalClose, null, /*#__PURE__*/ React.createElement("button", {
        className: "oultine-none dark:border-transparent dark:ring-0",
        onClick: onCrossButtonClick
    }, /*#__PURE__*/ React.createElement(XMarkIcon, {
        className: "h-11 w-11"
    })))))))));
}
